import { Injectable, NgZone } from '@angular/core';
import { Platform } from '@ionic/angular';

/**
 * KeyboardAssistService provides native-like Enter key behavior for form navigation,
 * primarily targeting mobile environments.
 */
@Injectable({ providedIn: 'root' })
export class KeyboardAssistService {
    private static readonly excludedInputTypes = new Set(['submit', 'button', 'checkbox', 'radio', 'file', 'hidden']);
    private attached = new WeakMap<HTMLElement, () => void>();
    private readonly noop = () => {
        /* no-op detach */
    };

    constructor(
        private readonly platform: Platform,
        private readonly ngZone: NgZone
    ) {}

    /**
     * Attaches keyboard navigation behavior to the nearest form within the given container.
     *
     * - Intercepts Enter presses to move focus to the next focusable field instead of submitting the form.
     * - Updates `enterkeyhint` on iOS to show native-like "Next"/"Done" labels on the virtual keyboard.
     *
     * Listeners are registered outside Angular's zone because this logic only performs DOM interactions and
     * does not depend on Angular state. This avoids triggering global change detection on every key/focus event.
     */
    attachTo(container: HTMLElement): () => void {
        const form = container.closest('form') ?? container.querySelector('form');
        if (!form) {
            return this.noop;
        }

        const existing = this.attached.get(form);
        if (existing) {
            existing();
        }

        const keydownHandler = this.handleKeyDown.bind(this);
        const focusinHandler = () => this.applyEnterKeyHints(form);

        // Register listeners outside Angular to avoid change detection on high-frequency events (keydown/focusin).
        this.ngZone.runOutsideAngular(() => {
            form.addEventListener('keydown', keydownHandler);
            // Use capture so we react even when focus events originate from components/wrappers (e.g. Ionic).
            form.addEventListener('focusin', focusinHandler, true);
        });

        this.applyEnterKeyHints(form);

        const detach = () => {
            form.removeEventListener('keydown', keydownHandler);
            form.removeEventListener('focusin', focusinHandler, true);
            if (this.attached.get(form) === detach) {
                this.attached.delete(form);
            }
        };

        this.attached.set(form, detach);

        return detach;
    }

    private handleKeyDown(event: KeyboardEvent): void {
        if (event.key !== 'Enter') {
            return;
        }

        const target = this.getNativeTarget(event);
        if (!target) {
            return;
        }

        const formEl = target.closest('form');
        if (!formEl) {
            return;
        }

        if (target instanceof HTMLTextAreaElement) {
            return;
        }

        if (target instanceof HTMLInputElement) {
            const type = (target.getAttribute('type') ?? '').toLowerCase();
            if (KeyboardAssistService.excludedInputTypes.has(type)) {
                return;
            }
        }

        const focusable = Array.from(
            formEl.querySelectorAll<HTMLElement>('input, textarea, select, [tabindex]')
        ).filter((el) => this.isFocusable(el));

        const currentIndex = focusable.indexOf(target);
        if (currentIndex === -1) {
            return;
        }

        // Prevent unintended form submit on Enter.
        event.preventDefault();

        const next = focusable[currentIndex + 1];
        if (!next) {
            return;
        }

        next.focus();
    }

    private isFocusable(el: HTMLElement): boolean {
        if (!el.isConnected) {
            return false;
        }

        const tag = el.tagName.toLowerCase();
        const isNative = tag === 'input' || tag === 'textarea' || tag === 'select' || tag === 'button';

        if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') {
            return false;
        }

        // Elements with tabindex="-1" are explicitly removed from the keyboard tab order.
        // They may be focusable programmatically, but must be excluded from enter/tab-based
        // navigation.
        if (el.getAttribute('tabindex') === '-1') {
            return false;
        }

        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }

        if (isNative && (el as HTMLInputElement).type === 'hidden') {
            return false;
        }

        return true;
    }

    private isIOS(): boolean {
        return this.platform.is('ios');
    }

    /**
     * Resolves the actual native form control that triggered the keyboard event. Walks
     * the event target and its composed path to reliably find the corresponding native
     * form control, ensuring consistent keyboard handling across both plain HTML forms
     * and Ionic components.
     */
    private getNativeTarget(event: KeyboardEvent): HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null {
        const direct = event.target;
        if (
            direct instanceof HTMLInputElement ||
            direct instanceof HTMLTextAreaElement ||
            direct instanceof HTMLSelectElement
        ) {
            return direct;
        }

        const path = event.composedPath?.() ?? [];
        for (const item of path) {
            if (
                item instanceof HTMLInputElement ||
                item instanceof HTMLTextAreaElement ||
                item instanceof HTMLSelectElement
            ) {
                return item;
            }

            if (item instanceof HTMLElement) {
                const tag = item.tagName.toLowerCase();
                if (tag === 'ion-input' || tag === 'ion-textarea' || tag === 'ion-select') {
                    const candidate = item.querySelector('input, textarea, select');
                    if (
                        candidate instanceof HTMLInputElement ||
                        candidate instanceof HTMLTextAreaElement ||
                        candidate instanceof HTMLSelectElement
                    ) {
                        return candidate;
                    }
                }
            }
        }

        return null;
    }

    /**
     * On iOS, the virtual keyboard shows context-aware action labels on the Enter key.
     * This method mirrors native form behavior by dynamically setting `enterkeyhint` on
     * each focusable control inside the form.
     */
    private applyEnterKeyHints(formEl: HTMLElement): void {
        if (!this.isIOS()) {
            return;
        }

        void this.applyEnterKeyHintsAsync(formEl);
    }

    /**
     * Applies `enterkeyhint` attributes to the native form controls inside the given form.
     *
     * This method is async because Ionic components render their underlying native inputs
     * asynchronously. Waiting for the next animation frame ensures that Shadow DOM content
     * and dynamically created native controls are fully initialized and can be reliably resolved.
     *
     * Without this async delay, some inputs may not yet exist in the DOM, resulting
     * in missing or incorrect `enterkeyhint` values.
     */
    private async applyEnterKeyHintsAsync(formEl: HTMLElement): Promise<void> {
        // Wait a tick so Ionic components have a chance to finish rendering
        // their internal native controls.
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

        const nodes = Array.from(
            formEl.querySelectorAll<HTMLElement>('input, textarea, select, ion-input, ion-textarea, [tabindex]')
        );

        const resolved: Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = [];

        for (const node of nodes) {
            if (
                node instanceof HTMLInputElement ||
                node instanceof HTMLTextAreaElement ||
                node instanceof HTMLSelectElement
            ) {
                resolved.push(node);
                continue;
            }

            const tag = node.tagName.toLowerCase();

            // Ionic wrappers native control is in Shadow DOM, so querySelector won't find it.
            if (
                (tag === 'ion-input' || tag === 'ion-textarea') &&
                typeof (node as any).getInputElement === 'function'
            ) {
                try {
                    const native = await (node as any).getInputElement();
                    if (native instanceof HTMLInputElement || native instanceof HTMLTextAreaElement) {
                        resolved.push(native);
                    }
                } catch {
                    // If Ionic fails to resolve the native element, skip it.
                }
                continue;
            }

            // Generic wrapper fallback (non-Ionic): try light-DOM lookup.
            const candidate = node.querySelector('input, textarea, select');
            if (
                candidate instanceof HTMLInputElement ||
                candidate instanceof HTMLTextAreaElement ||
                candidate instanceof HTMLSelectElement
            ) {
                resolved.push(candidate);
            }
        }

        // Filter using focus rules against the actual element that will receive focus.
        const focusable = resolved.filter((el) => this.isFocusable(el as unknown as HTMLElement));

        for (let i = 0; i < focusable.length; i++) {
            const el = focusable[i];

            // Textareas should keep their multiline Enter behavior.
            if (el instanceof HTMLTextAreaElement) {
                continue;
            }

            // Skip non-text inputs that should not participate in Next/Done navigation.
            const type = el instanceof HTMLInputElement ? (el.getAttribute('type') ?? '').toLowerCase() : '';
            if (el instanceof HTMLInputElement && KeyboardAssistService.excludedInputTypes.has(type)) {
                continue;
            }

            const hasNext = i < focusable.length - 1;
            el.setAttribute('enterkeyhint', hasNext ? 'next' : 'done');
        }
    }
}
